import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getSsoStatus, setSsoEnforced, type SsoProvider, type SsoStatus } from "../lib/api.ts";
import { authClient } from "../lib/auth-client.ts";
import { toast } from "../components/Toast.tsx";

/**
 * Single Sign-On settings (Enterprise). Provider registration + DNS domain
 * verification are the native @better-auth/sso endpoints, called through the
 * auth client; the capability gate, provider list, and "Require SSO" toggle are
 * CurrencyCore's product layer (/orgs/:id/sso). Shown only when the org is
 * SSO-entitled.
 */

type SsoResult = { data: unknown; error: { message?: string } | null };
// The native sso client methods are inferred from the server endpoints
// (/sso/register, /sso/request-domain-verification, /sso/verify-domain,
// /sso/delete-provider). Access through a narrow view to keep call sites typed.
const ssoApi = (authClient as unknown as {
  sso: {
    register: (b: Record<string, unknown>) => Promise<SsoResult>;
    requestDomainVerification: (b: Record<string, unknown>) => Promise<SsoResult>;
    verifyDomain: (b: Record<string, unknown>) => Promise<SsoResult>;
    deleteProvider: (b: Record<string, unknown>) => Promise<SsoResult>;
  };
}).sso;

function readDnsRecord(data: unknown, domain: string): { name: string; value: string } | null {
  if (!data || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  const name = (d.name ?? d.recordName ?? d.host ?? (d.identifier ? `${d.identifier}.${domain}` : null));
  const value =
    d.value ?? d.recordValue ?? d.txt ??
    (d.identifier && d.token ? `${d.identifier}=${d.token}` : null);
  if (!name || !value) return null;
  return { name: String(name), value: String(value) };
}

export function SsoSettingsPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const [status, setStatus] = useState<SsoStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const [issuer, setIssuer] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [domain, setDomain] = useState("");
  const [saving, setSaving] = useState(false);
  const [dns, setDns] = useState<{ domain: string; name: string; value: string } | null>(null);

  const refresh = useCallback(async () => {
    if (!orgId) return;
    try {
      setStatus(await getSsoStatus(orgId));
    } catch {
      /* best-effort */
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function fetchDns(d: string) {
    try {
      const res = await ssoApi.requestDomainVerification({ domain: d });
      const rec = readDnsRecord(res.data, d);
      if (rec) setDns({ domain: d, ...rec });
    } catch {
      /* leave dns null — the provider still shows as unverified */
    }
  }

  async function addProvider(e: React.FormEvent) {
    e.preventDefault();
    if (!orgId) return;
    const d = domain.trim().toLowerCase();
    setSaving(true);
    try {
      const providerId = `org-${orgId.slice(0, 8)}-${d.replace(/[^a-z0-9]+/g, "-")}`;
      const res = await ssoApi.register({
        providerId,
        issuer: issuer.trim(),
        domain: d,
        organizationId: orgId,
        oidcConfig: { clientId: clientId.trim(), clientSecret: clientSecret.trim() },
      });
      if (res.error) {
        toast(res.error.message ?? "Couldn't register the provider.");
      } else {
        toast("Provider added. Verify your domain to activate it.", true);
        setIssuer("");
        setClientId("");
        setClientSecret("");
        setDomain("");
        await fetchDns(d);
        await refresh();
      }
    } catch {
      toast("Couldn't register the provider.");
    } finally {
      setSaving(false);
    }
  }

  async function verify(d: string) {
    const res = await ssoApi.verifyDomain({ domain: d });
    if (res.error) {
      toast(res.error.message ?? "Domain not verified yet. DNS can take a few minutes.");
    } else {
      toast("Domain verified. SSO is active.", true);
      setDns(null);
      await refresh();
    }
  }

  async function remove(p: SsoProvider) {
    const res = await ssoApi.deleteProvider({ providerId: p.providerId });
    if (res.error) toast(res.error.message ?? "Couldn't remove the provider.");
    else {
      toast("Provider removed.", true);
      await refresh();
    }
  }

  async function toggleEnforce(next: boolean) {
    if (!orgId) return;
    try {
      await setSsoEnforced(orgId, next);
      setStatus((s) => (s ? { ...s, enforced: next } : s));
      toast(next ? "Require SSO enabled." : "Require SSO disabled.", true);
    } catch {
      toast("Couldn't update Require SSO.");
    }
  }

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="skeleton-page">
          {[0, 1].map((i) => <div key={i} className="skeleton" style={{ height: "5rem" }} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="page-head rise">
        <div>
          <h1>Single Sign-On</h1>
          <p className="sub">Let your team sign in through your company identity provider (OIDC).</p>
        </div>
      </div>

      {!status?.enabled ? (
        <div className="notice accent rise">
          <span className="ic">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" />
            </svg>
          </span>
          <span>
            SSO is an <strong style={{ color: "var(--text)" }}>Enterprise</strong> feature.
            Upgrade this organization to connect Okta, Microsoft Entra ID, Google Workspace, and other OIDC providers.
          </span>
        </div>
      ) : (
        <>
          {/* Providers */}
          <div className="card rise">
            <div className="card-head">
              <h2>Identity providers</h2>
              <span className="hint mono">{status.providers.length} configured</span>
            </div>
            {status.providers.length === 0 ? (
              <div className="card-pad" style={{ textAlign: "center", padding: "32px 20px" }}>
                <p className="muted">No providers yet. Add your IdP below.</p>
              </div>
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr><th>Domain</th><th>Issuer</th><th>Status</th><th /></tr>
                  </thead>
                  <tbody>
                    {status.providers.map((p) => (
                      <tr key={p.id}>
                        <td><span className="strong mono">{p.domain}</span></td>
                        <td><span className="muted mono" style={{ fontSize: "12px" }}>{p.issuer}</span></td>
                        <td>
                          {p.domainVerified ? (
                            <span className="badge badge-accent" style={{ fontSize: "10px" }}>Verified</span>
                          ) : (
                            <span className="badge badge-neutral" style={{ fontSize: "10px" }}>Unverified</span>
                          )}
                        </td>
                        <td className="col-right" style={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}>
                          {!p.domainVerified && (
                            <>
                              <button className="btn btn-sm" type="button" onClick={() => void fetchDns(p.domain)}>DNS record</button>
                              <button className="btn btn-sm" type="button" onClick={() => void verify(p.domain)}>Verify</button>
                            </>
                          )}
                          <button className="btn btn-sm btn-danger" type="button" onClick={() => void remove(p)}>Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {dns && (
              <div className="card-pad">
                <div className="notice rise" style={{ marginBottom: 0 }}>
                  <span>
                    To verify <strong className="mono">{dns.domain}</strong>, add this <strong>TXT</strong> record at your DNS provider, then click <strong>Verify</strong>:
                    <div className="secret-box" style={{ marginTop: 10 }}>
                      <code>{dns.name}  TXT  {dns.value}</code>
                    </div>
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Add provider */}
          <div className="card mt rise">
            <div className="card-head"><h2>Add a provider</h2></div>
            <form className="card-pad" onSubmit={(e) => void addProvider(e)}>
              <div className="form-row">
                <label className="form-label" htmlFor="sso-domain">Email domain</label>
                <input id="sso-domain" className="form-input" placeholder="acme.com" value={domain} onChange={(e) => setDomain(e.target.value)} required />
              </div>
              <div className="form-row">
                <label className="form-label" htmlFor="sso-issuer">Issuer URL (OIDC)</label>
                <input id="sso-issuer" className="form-input" placeholder="https://acme.okta.com" value={issuer} onChange={(e) => setIssuer(e.target.value)} required />
              </div>
              <div className="form-row">
                <label className="form-label" htmlFor="sso-client-id">Client ID</label>
                <input id="sso-client-id" className="form-input" value={clientId} onChange={(e) => setClientId(e.target.value)} required />
              </div>
              <div className="form-row">
                <label className="form-label" htmlFor="sso-client-secret">Client secret</label>
                <input id="sso-client-secret" className="form-input" type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} required />
              </div>
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? "Adding…" : "Add provider"}
              </button>
              {(() => {
                // Provider ID is derived from the org + email domain (same rule as
                // addProvider), so we can show the real callback URL live.
                const pid =
                  orgId && domain.trim()
                    ? `org-${orgId.slice(0, 8)}-${domain.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-")}`
                    : "";
                const callback = `https://api.currency-core.com/api/auth/sso/callback/${pid || "<providerId>"}`;
                return (
                  <div className="hint" style={{ marginTop: 12, display: "grid", gap: 6 }}>
                    <p style={{ margin: 0 }}>
                      <strong>Provider ID</strong> is the unique identifier for this SSO connection.
                      CurrencyCore generates it for you from your organization + email domain, and you
                      don't choose it. It's the last path segment of the callback URL below.
                    </p>
                    <p style={{ margin: 0 }}>
                      In your identity provider (Okta, Entra ID, Google Workspace, …), set the{" "}
                      <strong>redirect / callback URL</strong> (a.k.a. sign-in redirect URI) to:
                    </p>
                    <code className="mono" style={{ wordBreak: "break-all" }}>{callback}</code>
                    {pid && (
                      <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                        Provider ID for <span className="mono">{domain.trim()}</span>:{" "}
                        <span className="mono">{pid}</span>
                      </p>
                    )}
                  </div>
                );
              })()}
            </form>
          </div>

          {/* Require SSO */}
          <div className="card mt rise">
            <div className="card-head"><h2>Require SSO</h2></div>
            <div className="card-pad" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
              <p className="muted" style={{ margin: 0 }}>
                When on, anyone on a verified SSO domain must sign in via SSO. Password and Google
                sign-in are blocked for them. Organization owners are always exempt.
              </p>
              <label className="switch" aria-label="Require SSO">
                <input type="checkbox" checked={status.enforced} onChange={(e) => void toggleEnforce(e.target.checked)} />
                <span className="track" />
                <span className="thumb" />
              </label>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
